/**
 * `flow_id` — the join key that bridges the gap before a session exists.
 *
 * A capability tile click on the dashboard is the first beat of a journey, but
 * at that moment there is no tookii session to key it on: the tile only opens a
 * wizard, and the session row is created later (or never, if the user drops
 * out). Stamping a tab-scoped id on the click and re-reading it on the events
 * that follow — `audit_submitted` above all — is what stitches
 * "clicked First Impression" to the run it turned into.
 *
 * sessionStorage, not localStorage: one flow per tab, and it dies with the tab.
 * A new tile click overwrites the previous id — the most recent intent is the
 * one that owns whatever run comes next.
 */
const FLOW_ID_KEY = 'tookii_flow_id'

function newId(): string {
  // randomUUID needs a secure context; the fallback keeps a non-https preview
  // deploy from throwing inside a click handler.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `flow_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

/** Mint a new flow id for this tab and return it. Call on the entry click. */
export function startFlow(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const id = newId()
  try {
    window.sessionStorage.setItem(FLOW_ID_KEY, id)
  } catch {
    // Private-mode / blocked storage — the event still carries the id it was
    // minted with, only the stitch to later events is lost.
  }
  return id
}

/** The current tab's flow id, if a flow was started. */
export function currentFlowId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.sessionStorage.getItem(FLOW_ID_KEY) ?? undefined
  } catch {
    return undefined
  }
}
